import React, { useState, useEffect } from 'react';
import { Building2, Plus, Edit2, Trash2, MessageSquare, Mail, Smartphone, Check, Shield, Palette, ExternalLink, Copy } from 'lucide-react';
import { getAuthorizedHeaders } from '../utils/authorizedHeaders';

const API_BASE = 'https://warngen-api-352514293915.europe-west1.run.app';


const COLOR_OPTIONS = [
    { label: 'Esmeralda (RCV Finance)', value: '#059669', bgClass: 'bg-emerald-600' },
    { label: 'Azul Institucional', value: '#1E3A8A', bgClass: 'bg-blue-800' },
    { label: 'Índigo Corporativo', value: '#4F46E5', bgClass: 'bg-indigo-600' },
    { label: 'Púrpura / Morado', value: '#7C3AED', bgClass: 'bg-purple-600' },
    { label: 'Ámbar / Naranja', value: '#D97706', bgClass: 'bg-amber-600' },
    { label: 'Gris Oscuro', value: '#374151', bgClass: 'bg-gray-700' },
];

export default function ClientesManager({ usuario, clienteActivo, setClienteActivo }) {
    const [clientes, setClientes] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [mensaje, setMensaje] = useState('');
    const [error, setError] = useState('');
    const [modalAbierto, setModalAbierto] = useState(false);
    const [clienteEditando, setClienteEditando] = useState(null);

    const [form, setForm] = useState({
        nombre: '',
        codigo: '',
        descripcion: '',
        sms_sender_id: '',
        sms_number: '',
        whatsapp_number: '',
        email_remitente: '',
        email_nombre: '',
        color_tema: '#059669',
        email_template: ''
    });

    // Números Twilio disponibles como remitentes
    const [remitentes, setRemitentes] = useState({ numeros: [], sms_por_defecto: null, whatsapp_por_defecto: null });

    const esSuperAdmin = usuario?.rol === 'superadmin';

    const limpiarWa = (v) => (v || '').replace('whatsapp:', '').trim();
    const toSlug = (str) => String(str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const getCanalUrl = (cl) => `${window.location.origin}/canal/${toSlug(cl.nombre || cl.codigo)}`;
    const copiarUrlCanal = (cl) => {
        const url = getCanalUrl(cl);
        navigator.clipboard.writeText(url);
        setMensaje(`Enlace de verificación para Meta copiado: ${url}`);
        setTimeout(() => setMensaje(''), 4000);
    };


    const fetchClientes = async () => {
        setCargando(true);
        try {
            const res = await fetch(`${API_BASE}/api/clientes`, {
                headers: getAuthorizedHeaders()
            });
            const data = await res.json();
            if (data.success) {
                setClientes(data.data || []);
            }
        } catch (e) {
            setError('Error al cargar la lista de clientes');
        }
        setCargando(false);
    };

    useEffect(() => {
        fetchClientes();
        fetch(`${API_BASE}/api/remitentes`, { headers: getAuthorizedHeaders() })
            .then(r => r.json())
            .then(d => { if (d.success) setRemitentes(d.data); })
            .catch(() => {});
    }, []);

    const abrirCrear = () => {
        setClienteEditando(null);
        setForm({
            nombre: '',
            codigo: '',
            descripcion: '',
            sms_sender_id: '',
            sms_number: '',
            whatsapp_number: '',
            email_remitente: '',
            email_nombre: '',
        color_tema: '#059669',
        email_template: ''
        });
        setError('');
        setModalAbierto(true);
    };

    const abrirEditar = (cl) => {
        setClienteEditando(cl);
        setForm({
            nombre: cl.nombre || '',
            codigo: cl.codigo || '',
            descripcion: cl.descripcion || '',
            sms_sender_id: cl.sms_sender_id || '',
            sms_number: cl.sms_number || '',
            whatsapp_number: limpiarWa(cl.whatsapp_number),
            email_remitente: cl.email_remitente || '',
            email_nombre: cl.email_nombre || '',
            color_tema: cl.color_tema || '#059669',
            email_template: cl.email_template || ''
        });
        setError('');
        setModalAbierto(true);
    };

    const guardarCliente = async (e) => {
        e.preventDefault();
        setError('');
        setMensaje('');

        if (form.sms_sender_id && form.sms_sender_id.length > 11) {
            setError('El Emisor SMS (Alphanumeric Sender ID) no puede tener más de 11 caracteres en Twilio.');
            return;
        }

        try {
            const url = clienteEditando 
                ? `${API_BASE}/api/clientes/${clienteEditando.id}`
                : `${API_BASE}/api/clientes`;
            const method = clienteEditando ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: getAuthorizedHeaders(),
                body: JSON.stringify(form)
            });
            const data = await res.json();
            if (data.success) {
                setMensaje(clienteEditando ? 'Cliente actualizado con éxito' : 'Cliente creado con éxito');
                setModalAbierto(false);
                fetchClientes();
                setTimeout(() => setMensaje(''), 4000);
            } else {
                setError(data.error || 'Error al guardar el cliente');
            }
        } catch (e) {
            setError('Error de conexión con el servidor');
        }
    };

    const eliminarCliente = async (id) => {
        if (!window.confirm('¿Seguro que deseas desactivar este cliente?')) return;
        try {
            const res = await fetch(`${API_BASE}/api/clientes/${id}`, {
                method: 'DELETE',
                headers: getAuthorizedHeaders()
            });
            const data = await res.json();
            if (data.success) {
                setMensaje('Cliente desactivado');
                fetchClientes();
                setTimeout(() => setMensaje(''), 3000);
            } else {
                setError(data.error || 'Error al desactivar');
            }
        } catch (e) {
            setError('Error de conexión');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                        <Building2 className="text-primary-600 dark:text-primary-400" />
                        Gestión de Clientes y Emisores
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Configura cada cliente / departamento con sus senders específicos (SMS Twilio, WhatsApp y Email) y estética personalizada.
                    </p>
                </div>

                {esSuperAdmin && (
                    <button
                        onClick={abrirCrear}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition flex items-center gap-2"
                    >
                        <Plus size={18} />
                        Nuevo Cliente
                    </button>
                )}
            </div>

            {mensaje && (
                <div className="p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 rounded-lg text-sm">
                    {mensaje}
                </div>
            )}
            {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* LISTADO DE CLIENTES */}
            {cargando ? (
                <div className="text-center py-12 text-gray-500">Cargando clientes...</div>
            ) : clientes.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow text-center text-gray-500">
                    No hay clientes registrados aún.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {clientes.map((cl) => {
                        const esActivo = clienteActivo?.id === cl.id;
                        return (
                            <div
                                key={cl.id}
                                className={`bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border-2 transition hover:shadow-lg flex flex-col justify-between ${
                                    esActivo ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-900' : 'border-gray-200 dark:border-gray-700'
                                }`}
                            >
                                <div className="p-5 space-y-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-sm"
                                                style={{ backgroundColor: cl.color_tema || '#059669' }}
                                            >
                                                {cl.nombre.substring(0, 2).toUpperCase()}
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-gray-900 dark:text-gray-100 text-lg leading-tight">
                                                    {cl.nombre}
                                                </h3>
                                                <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                                                    {cl.codigo}
                                                </span>
                                            </div>
                                        </div>

                                        {esActivo && (
                                            <span className="bg-primary-100 text-primary-800 dark:bg-primary-900/60 dark:text-primary-300 text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1">
                                                <Check size={12} /> Activo
                                            </span>
                                        )}
                                    </div>

                                    {cl.descripcion && (
                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                            {cl.descripcion}
                                        </p>
                                    )}

                                    {/* CANALES Y SENDERS */}
                                    <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-gray-700 text-xs">
                                        <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 font-medium">
                                                <MessageSquare size={14} className="text-amber-500" />
                                                <span>SMS Sender:</span>
                                            </div>
                                            <span className="font-mono font-bold text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 px-2 py-0.5 rounded border dark:border-gray-600">
                                                {cl.sms_number || cl.sms_sender_id || 'Número por defecto'}
                                            </span>
                                        </div>

                                        <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 font-medium">
                                                <Mail size={14} className="text-blue-500" />
                                                <span>Email:</span>
                                            </div>
                                            <span className="text-gray-900 dark:text-gray-100 truncate max-w-[170px]" title={cl.email_remitente}>
                                                {cl.email_nombre ? `${cl.email_nombre}` : (cl.email_remitente || 'Por defecto')}
                                            </span>
                                        </div>

                                        <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 font-medium">
                                                <Smartphone size={14} className="text-green-500" />
                                                <span>WhatsApp:</span>
                                            </div>
                                            <span className="font-mono text-gray-900 dark:text-gray-100">
                                                {cl.whatsapp_number ? cl.whatsapp_number.replace('whatsapp:', '') : 'Por defecto'}
                                            </span>
                                        </div>

                                        {/* Enlace público de verificación para Meta Display Name */}
                                        <div className="p-2 bg-emerald-50/70 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg space-y-1">
                                            <div className="flex items-center justify-between">
                                                <span className="text-[11px] font-semibold text-emerald-800 dark:text-emerald-300">
                                                    URL para aprobar nombre en Meta:
                                                </span>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => copiarUrlCanal(cl)}
                                                        className="px-2 py-0.5 bg-white dark:bg-gray-800 border border-emerald-300 dark:border-emerald-700 rounded text-[11px] font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 flex items-center gap-1"
                                                        title="Copiar URL para pegar en Meta Business"
                                                    >
                                                        <Copy size={11} /> Copiar
                                                    </button>
                                                    <a
                                                        href={getCanalUrl(cl)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-1 bg-white dark:bg-gray-800 border border-emerald-300 dark:border-emerald-700 rounded text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100"
                                                        title="Abrir página pública del canal"
                                                    >
                                                        <ExternalLink size={11} />
                                                    </a>
                                                </div>
                                            </div>
                                            <div className="font-mono text-[10px] text-gray-600 dark:text-gray-400 truncate">
                                                {getCanalUrl(cl)}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-3 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                                    <button
                                        onClick={() => setClienteActivo(cl)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                            esActivo 
                                                ? 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 cursor-default' 
                                                : 'bg-primary-600 hover:bg-primary-700 text-white shadow-sm'
                                        }`}
                                    >
                                        {esActivo ? 'Cliente Seleccionado' : 'Seleccionar como Activo'}
                                    </button>

                                    {esSuperAdmin && (
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => abrirEditar(cl)}
                                                className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                                                title="Editar cliente"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                            <button
                                                onClick={() => eliminarCliente(cl.id)}
                                                className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                                                title="Desactivar cliente"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* MODAL CREAR / EDITAR CLIENTE */}
            {modalAbierto && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6 space-y-5">
                        <div className="flex items-center justify-between border-b pb-3 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                <Building2 className="text-primary-600" />
                                {clienteEditando ? 'Editar Cliente / Organización' : 'Nuevo Cliente / Organización'}
                            </h3>
                            <button
                                onClick={() => setModalAbierto(false)}
                                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={guardarCliente} className="space-y-4 text-sm">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Nombre del Cliente *
                                    </label>
                                    <input
                                        type="text"
                                        value={form.nombre}
                                        onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                        placeholder="Ej: RCV Finance"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Código Identificador *
                                    </label>
                                    <input
                                        type="text"
                                        value={form.codigo}
                                        onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
                                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 font-mono"
                                        placeholder="RCV_FINANCE"
                                        disabled={!!clienteEditando}
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Descripción
                                </label>
                                <textarea
                                    value={form.descripcion}
                                    onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                    rows={2}
                                    placeholder="Descripción del cliente o departamento"
                                />
                            </div>

                            {/* CONFIGURACIÓN DE SENDERS MULTICANAL */}
                            <div className="p-4 bg-gray-50 dark:bg-gray-700/40 rounded-xl space-y-4 border dark:border-gray-600">
                                <h4 className="font-bold text-gray-800 dark:text-gray-200 text-xs uppercase tracking-wider flex items-center gap-1.5">
                                    <Shield size={14} className="text-primary-600" />
                                    Remitentes de Alertas (Senders)
                                </h4>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Número para SMS
                                    </label>
                                    <select
                                        value={form.sms_number}
                                        onChange={(e) => setForm({ ...form, sms_number: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 font-mono"
                                    >
                                        <option value="">Por defecto del sistema ({remitentes.sms_por_defecto || 'sin configurar'})</option>
                                        {remitentes.numeros
                                            .filter(n => n.sms && (form.codigo === 'MALAGA_RESP' ? n.numero === '+16185905357' : n.numero !== '+16185905357'))
                                            .map(n => (
                                                <option key={n.numero} value={n.numero}>{n.numero} — {n.nombre}</option>
                                            ))}
                                    </select>
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        {form.codigo === 'MALAGA_RESP'
                                            ? 'Número exclusivo asignado a Málaga Responde (+1 618-590-5357).'
                                            : 'Número Twilio desde el que se envían los SMS de este cliente.'}
                                    </p>
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                                            Nombre de remitente SMS (opcional)
                                        </label>
                                        <span className="text-[11px] text-gray-500">
                                            {form.sms_sender_id.length}/11 caracteres
                                        </span>
                                    </div>
                                    <input
                                        type="text"
                                        maxLength={11}
                                        value={form.sms_sender_id}
                                        onChange={(e) => setForm({ ...form, sms_sender_id: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 font-mono font-semibold text-primary-700 dark:text-primary-300"
                                        placeholder="RCV Finance"
                                    />
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        Solo se muestra si está registrado (en España requiere registro CNMC). Si el operador lo rechaza, se envía desde el número de arriba. Déjalo vacío para usar siempre el número.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                            Nombre Remitente Email
                                        </label>
                                        <input
                                            type="text"
                                            value={form.email_nombre}
                                            onChange={(e) => setForm({ ...form, email_nombre: e.target.value })}
                                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                            placeholder="RCV Finance Alertas"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                            Email Remitente (From)
                                        </label>
                                        <input
                                            type="email"
                                            value={form.email_remitente}
                                            onChange={(e) => setForm({ ...form, email_remitente: e.target.value })}
                                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                            placeholder="alertas@rcvfinance.com"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Número WhatsApp (remitente verificado en Twilio)
                                    </label>
                                    <select
                                        value={form.whatsapp_number}
                                        onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 font-mono"
                                    >
                                        <option value="">Por defecto del sistema ({remitentes.whatsapp_por_defecto || 'sin configurar'})</option>
                                        {remitentes.numeros
                                            .filter(n => form.codigo === 'MALAGA_RESP' ? n.numero === '+16185905357' : n.numero !== '+16185905357')
                                            .map(n => (
                                                <option key={n.numero} value={n.numero}>{n.numero} — {n.nombre}</option>
                                            ))}
                                    </select>
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        {form.codigo === 'MALAGA_RESP'
                                            ? 'Número exclusivo de WhatsApp asignado a Málaga Responde (+1 618-590-5357).'
                                            : 'El número debe estar dado de alta como WhatsApp Sender en Twilio (estado Online).'}
                                    </p>
                                </div>
                            </div>

                            {/* COLOR / ESTÉTICA */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                                    <Palette size={14} /> Color Distintivo del Cliente
                                </label>
                                <div className="flex flex-wrap gap-3">
                                    {COLOR_OPTIONS.map((col) => (
                                        <button
                                            key={col.value}
                                            type="button"
                                            onClick={() => setForm({ ...form, color_tema: col.value })}
                                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition ${
                                                form.color_tema === col.value
                                                    ? 'border-primary-500 ring-2 ring-primary-300 bg-gray-50 dark:bg-gray-700'
                                                    : 'border-gray-200 dark:border-gray-600'
                                            }`}
                                        >
                                            <span className={`w-3.5 h-3.5 rounded-full ${col.bgClass}`} />
                                            {col.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
                                <button
                                    type="button"
                                    onClick={() => setModalAbierto(false)}
                                    className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="bg-primary-600 hover:bg-primary-700 text-white px-5 py-2 rounded-lg font-semibold shadow-sm"
                                >
                                    {clienteEditando ? 'Guardar Cambios' : 'Crear Cliente'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}